import React, { useState } from 'react'
import domPurify from 'dompurify'
import { TextField } from '@mui/material'
import { ApolloError, useMutation } from '@apollo/client'
import { Button, colors } from '@resiliatech/birdsong'
import { CREATE_INCOMPLETE_USER_SURVEYS } from 'Apollo/Mutations/CreateUserSurveys'
import {
  Actions,
  ErrorMessage,
  Feedback,
  Header,
  NeedsAssessmentTriggerContainer,
  SuccessMessage,
  Text,
} from './NeedsAssessmentTrigger.styled'
import { Ribbon } from '../_common/Ribbon'

const FEEDBACK_TIMEOUT = 10000

export const NeedsAssessmentTrigger = () => {
  const [userIdsString, setUserIdsString] = useState('')
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string>()

  const [createIncompleteUserSurveys, { loading }] = useMutation(
    CREATE_INCOMPLETE_USER_SURVEYS
  )

  const areIdsValid = () => {
    let lastGoodId: number | null = null
    const ids = Array.from(new Set(userIdsString.split(',')))

    // Track the last good ID to provide a better error message
    for (let i = 0; i < ids.length; i++) {
      const currentId = parseInt(ids[i].trim(), 10)
      if (!Number.isNaN(currentId)) lastGoodId = currentId
      else return [false, lastGoodId]
    }
    return [true, lastGoodId]
  }

  const resetFeedback = () => {
    setError('')
    setSuccess('')
  }

  const handleFeedback = (message: string, type: 'error' | 'success') => {
    if (type === 'error') setError(message)
    else setSuccess(message)
    setTimeout(() => resetFeedback(), FEEDBACK_TIMEOUT)
  }

  const handleSubmit = async () => {
    resetFeedback()
    const [isValid, lastGoodId] = areIdsValid()
    if (!isValid) {
      handleFeedback(`Invalid user ID near ${lastGoodId}`, 'error')
      return
    }

    const userIds = userIdsString
      .split(',')
      .filter((id) => !!id)
      .map((id) => parseInt(id.trim(), 10))

    // Remove any duplicates from the comma-separated list
    const uniqueUserIds = Array.from(new Set(userIds))
    // Track the duplicates
    const duplicateUserIds = Array.from(
      new Set(
        userIds.filter((id) => userIds.indexOf(id) !== userIds.lastIndexOf(id))
      )
    )
    // Track IDs that resolver filters out
    const idsFilteredOut: number[] = []

    if (uniqueUserIds.length) {
      try {
        const {
          data: {
            createIncompleteUserSurveys: { userIds: mutationIds = [] } = {},
          } = {},
        } = await createIncompleteUserSurveys({
          variables: { userIds: uniqueUserIds },
        })

        // No user surveys created
        if (!mutationIds.length) {
          handleFeedback('Failed to create surveys for any user IDs', 'error')
          return
        }

        // Some user surveys created
        let successMessage = `Successfully created surveys for ${
          mutationIds.length
        } user ID${mutationIds.length > 1 ? 's' : ''}.`

        // Tack on any duplicate user IDs that were found (created or not)
        if (duplicateUserIds.length) {
          successMessage += ` Duplicate IDs found (not count):`
          duplicateUserIds.forEach((id) => {
            successMessage += ` ${id},`
          })
          successMessage = `${successMessage.slice(0, -1)}.`
        }

        // Tack on any user IDs that were not created in resolver
        if (mutationIds.length !== uniqueUserIds.length) {
          successMessage += ` Failed to create surveys for ${
            uniqueUserIds.length - mutationIds.length
          } unique user IDs (these were likely non-enterprise accounts that were filtered out):`

          uniqueUserIds.forEach((id) => {
            if (!mutationIds.includes(id)) {
              successMessage += ` ${id},`
              idsFilteredOut.push(id)
            }
          })

          successMessage = successMessage.slice(0, -1)
          handleFeedback(successMessage, 'success')
        }

        console.info(
          successMessage,
          '\nUser IDs submitted for survey creation: ',
          userIds,
          '\nDuplicate user IDs found in user input: ',
          duplicateUserIds,
          '\nUser IDs where survey was not created by resolver:',
          idsFilteredOut,
          '\nUser IDs where survey was successfully created: ',
          mutationIds
        )
        handleFeedback(successMessage, 'success')
        setUserIdsString('')
      } catch (e) {
        const catchMessage =
          (e as ApolloError)?.message ||
          'Unknown error. Check network requests.'
        handleFeedback(catchMessage, 'error')
      }
    } else {
      handleFeedback('No user IDs provided', 'error')
    }
  }

  const isCreateGoalGroupButtonDisabled = !userIdsString

  return (
    <NeedsAssessmentTriggerContainer>
      <Ribbon />
      <Header>Needs Assessment - Trigger New Surveys for Users</Header>
      <Text>
        Use this tool to pass comma-separated user IDs (platform IDs from the
        User table) to instantly create blank Needs Assessment entries in the
        database. By creating these records, users will be prompted to take the
        survey on their next login.
      </Text>
      <Text>
        Before a successful run, it is recommended that a query be run against
        the UserSurvey table to see if any completed surveys exist (the primary
        use case for this tool is existing users who do not have a survey but
        need one).
      </Text>
      <Text>
        After a successful run, it is recommended that a query be run against
        the production database to ensure the UserSurvey records were created
        successfully and an export of that data be added to the request
        spreadsheet attached to the Asana ticket (if applicable).
      </Text>
      <Actions>
        <Button
          isDisabled={isCreateGoalGroupButtonDisabled || loading}
          isLoading={loading}
          label='Create User Surveys'
          onClick={handleSubmit}
          type='button'
          variant='primary'
        />
        <Button
          isDisabled={isCreateGoalGroupButtonDisabled || loading}
          label='Clear'
          onClick={() => setUserIdsString('')}
          type='button'
          variant='primary'
        />
      </Actions>
      <Feedback>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
      </Feedback>
      <TextField
        id='needsAsssessmentTriggerIds'
        disabled={loading}
        label='User IDs (e.g. 123, 456, 789)'
        onChange={({ target: { value } }) => setUserIdsString(value)}
        required
        size='small'
        multiline
        sx={{
          backgroundColor: colors.background.secondaryBackground,
          height: 'auto',
          width: '900px',
        }}
        value={domPurify.sanitize(userIdsString)}
        variant='outlined'
      />
    </NeedsAssessmentTriggerContainer>
  )
}
